/**
 * One-suggestion-per-set bottom sheet. Shown only when the form bar was
 * RED or AMBER for ≥30% of the set duration on a supported exercise.
 *
 * Auto-dismiss after 8s with a depleting bar; swipe-down or tap "Got it"
 * dismisses early.
 */
import { useEffect, useState } from "react";

const SUGGESTIONS = {
  knee_cave: {
    headline: "Push your knees outward as you lower.",
    issue: "Your knees collapsed inward during the descent.",
    why: "Knee cave shifts load into the joint instead of the muscles, raising injury risk.",
    fixes: [
      "Plant your weight on the outside of your feet.",
      "Drive your knees out so they track over your toes.",
    ],
  },
  hip_sag: {
    headline: "Keep your hips level throughout the movement.",
    issue: "Your hips dropped below shoulder-ankle line in push-ups.",
    why: "Hip sag overloads the lower back and weakens the chest contraction.",
    fixes: [
      "Squeeze your glutes as you press up.",
      "Brace your core like you're about to be punched.",
    ],
  },
  hip_deviation: {
    headline: "Lower your hips slightly to form a straight line.",
    issue: "Your hips were riding too high or too low in the plank.",
    why: "Hip drift reduces core load — the plank is no longer training what it should.",
    fixes: [
      "Imagine a straight line from shoulders to ankles.",
      "Tuck your tailbone under and engage glutes.",
    ],
  },
};

function BodyDiagram({ issueKey }) {
  // Minimal SVG, 56px tall. Highlights the affected joint.
  const accent = "#00D4FF";
  return (
    <svg viewBox="0 0 80 56" width="80" height="56" aria-hidden="true">
      {/* base figure */}
      <g stroke="#A09C97" strokeWidth="1.6" fill="none" strokeLinecap="round">
        <circle cx="40" cy="10" r="4" />
        <line x1="40" y1="14" x2="40" y2="34" />
        <line x1="40" y1="18" x2="32" y2="28" />
        <line x1="40" y1="18" x2="48" y2="28" />
        <line x1="40" y1="34" x2="33" y2="48" />
        <line x1="40" y1="34" x2="47" y2="48" />
      </g>
      {/* highlight + arrow */}
      {issueKey === "knee_cave" && (
        <g>
          <circle cx="36" cy="42" r="3.6" fill={accent} />
          <line x1="33" y1="42" x2="28" y2="42" stroke={accent} strokeWidth="2" markerEnd="url(#arrow)" />
        </g>
      )}
      {issueKey === "hip_sag" && (
        <g>
          <circle cx="40" cy="34" r="3.6" fill={accent} />
          <line x1="40" y1="38" x2="40" y2="32" stroke={accent} strokeWidth="2" markerEnd="url(#arrow)" />
        </g>
      )}
      {issueKey === "hip_deviation" && (
        <g>
          <circle cx="40" cy="34" r="3.6" fill={accent} />
          <line x1="34" y1="34" x2="46" y2="34" stroke={accent} strokeWidth="2" />
        </g>
      )}
      <defs>
        <marker id="arrow" viewBox="0 0 6 6" refX="3" refY="3" markerWidth="4" markerHeight="4" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={accent} />
        </marker>
      </defs>
    </svg>
  );
}

export default function SuggestionSheet({ issueKey, onDismiss }) {
  const data = SUGGESTIONS[issueKey];
  const [showWhy, setShowWhy] = useState(false);
  const [touchStartY, setTouchStartY] = useState(null);

  useEffect(() => {
    if (!data) return;
    const t = setTimeout(onDismiss, 8000);
    return () => clearTimeout(t);
  }, [issueKey, onDismiss, data]);

  if (!data) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[70]"
      data-testid={`pat-suggestion-${issueKey}`}
      onTouchStart={(e) => setTouchStartY(e.touches[0].clientY)}
      onTouchMove={(e) => {
        if (touchStartY != null && e.touches[0].clientY - touchStartY > 60) {
          onDismiss();
          setTouchStartY(null);
        }
      }}
    >
      <div
        className="mx-auto max-w-md bg-white"
        style={{
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: "16px 20px 24px",
          boxShadow: "0 -4px 16px rgba(0,0,0,0.08)",
          maxHeight: "38vh",
          overflowY: "auto",
        }}
      >
        {!showWhy ? (
          <>
            <div className="flex items-center gap-3">
              <BodyDiagram issueKey={issueKey} />
              <h3
                className="font-barlow-cond"
                style={{ fontSize: 20, fontWeight: 700, color: "#1A1A1A", lineHeight: 1.15 }}
              >
                {data.headline}
              </h3>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={onDismiss}
                data-testid="pat-suggestion-got-it"
                className="flex-1 h-11 rounded-pill border border-line2 text-[13px] font-semibold"
                style={{ borderRadius: 100 }}
              >
                Got it
              </button>
              <button
                onClick={() => setShowWhy(true)}
                data-testid="pat-suggestion-why"
                className="flex-1 h-11 rounded-pill text-[13px] font-bold"
                style={{ background: "#ECEAE7", color: "#1A1A1A", borderRadius: 100 }}
              >
                Why?
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="font-barlow-cond" style={{ fontSize: 20, fontWeight: 700 }}>
              Why this matters
            </h3>
            <p className="text-[13px] mt-2" style={{ color: "#1A1A1A" }}>
              <span className="font-semibold">Issue:</span> {data.issue}
            </p>
            <p className="text-[13px] mt-1" style={{ color: "#1A1A1A" }}>
              <span className="font-semibold">Why:</span> {data.why}
            </p>
            <ul className="mt-2 space-y-0.5 list-disc pl-5 text-[12.5px]" style={{ color: "#6B6760" }}>
              {data.fixes.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
            <button
              onClick={onDismiss}
              data-testid="pat-suggestion-close"
              className="mt-4 w-full h-11 rounded-pill text-[13px] font-bold"
              style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
            >
              Close
            </button>
          </>
        )}

        {/* Depleting timer bar */}
        <div className="mt-3 h-[3px] bg-canvas2 rounded-full overflow-hidden" data-testid="pat-suggestion-timer">
          <div
            style={{
              height: "100%",
              background: "#00D4FF",
              width: "100%",
              animation: "pat-deplete 8s linear forwards",
            }}
          />
        </div>
      </div>
      <style>{`@keyframes pat-deplete { from { width: 100%; } to { width: 0%; } }`}</style>
    </div>
  );
}
